import { apiClient } from '@/api/client';
import { Business } from '@/types/business';
import { MenuItem } from '@/types/menu-item';
import { mapBusinessWireToDomain } from '../mappers/business';
import { mapMenuItemWireToDomain } from '../mappers/menu-item';
import { businessWireSchema, paginatedBusinessResponseSchema, type BusinessWire } from '../schemas/business';
import { menuItemWireSchema, paginatedMenuItemResponseSchema } from '../schemas/menu-item';
import { MenuItemWire, PaginatedMenuItemResponse } from './menu-items';

/**
 * Admin API endpoints for menu item management
 */

export interface ListPendingMenuItemsParams {
  page?: number;
  per_page?: number;
  sort_by?: 'name' | 'created_at' | 'updated_at';
  sort_order?: 'asc' | 'desc';
}

function buildPendingQueryString(params: ListPendingMenuItemsParams): string {
  const queryParams: string[] = ['status=pending'];

  if (params.page !== undefined) {
    queryParams.push(`page=${params.page}`);
  }
  if (params.per_page !== undefined) {
    queryParams.push(`per_page=${params.per_page}`);
  }
  if (params.sort_by) {
    queryParams.push(`sort_by=${params.sort_by}`);
  }
  if (params.sort_order) {
    queryParams.push(`sort_order=${params.sort_order}`);
  }

  return `?${queryParams.join('&')}`;
}

/**
 * List pending menu items for admin review
 */
export async function listPendingMenuItems(
  params: ListPendingMenuItemsParams = {}
): Promise<PaginatedMenuItemResponse> {
  const queryParams: ListPendingMenuItemsParams = {
    page: 1,
    per_page: 20,
    sort_by: 'created_at',
    sort_order: 'desc',
    ...params,
  };

  const queryString = buildPendingQueryString(queryParams);
  const endpoint = `/api/menu-items${queryString}`;

  const wireData = await apiClient.get<{
    items: MenuItemWire[];
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  }>(endpoint);

  const validatedData = paginatedMenuItemResponseSchema.parse(wireData);

  return {
    items: validatedData.items.map(mapMenuItemWireToDomain),
    total: validatedData.total,
    page: validatedData.page,
    per_page: validatedData.per_page,
    total_pages: validatedData.total_pages,
  };
}

/**
 * Approve a pending menu item
 */
export async function approveMenuItem(id: string): Promise<MenuItem> {
  const wireData = await apiClient.patch<MenuItemWire>(`/api/menu-items/${id}/approve`);
  const validatedData = menuItemWireSchema.parse(wireData);
  return mapMenuItemWireToDomain(validatedData);
}

/**
 * Update a menu item (admin)
 */
export async function updateMenuItem(
  id: string,
  data: { name?: string; description?: string }
): Promise<MenuItem> {
  const wireData = await apiClient.put<MenuItemWire>(`/api/menu-items/${id}`, data);
  const validatedData = menuItemWireSchema.parse(wireData);
  return mapMenuItemWireToDomain(validatedData);
}

/**
 * Delete a menu item (admin)
 */
export async function deleteMenuItem(id: string): Promise<void> {
  await apiClient.delete(`/api/menu-items/${id}`);
}

// ===========================
// Admin Business Endpoints
// ===========================

export interface ListPendingBusinessesParams {
  page?: number;
  per_page?: number;
  sort_by?: 'name' | 'created_at';
  sort_order?: 'asc' | 'desc';
}

export interface PaginatedBusinessResponse {
  items: Business[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

function buildPendingBusinessQueryString(params: ListPendingBusinessesParams): string {
  const queryParams: string[] = ['status=pending'];

  if (params.page !== undefined) {
    queryParams.push(`page=${params.page}`);
  }
  if (params.per_page !== undefined) {
    queryParams.push(`per_page=${params.per_page}`);
  }
  if (params.sort_by) {
    queryParams.push(`sort_by=${params.sort_by}`);
  }
  if (params.sort_order) {
    queryParams.push(`sort_order=${params.sort_order}`);
  }

  return `?${queryParams.join('&')}`;
}

/**
 * List pending businesses for admin review
 */
export async function listPendingBusinesses(
  params: ListPendingBusinessesParams = {}
): Promise<PaginatedBusinessResponse> {
  const queryParams: ListPendingBusinessesParams = {
    page: 1,
    per_page: 20,
    sort_by: 'created_at',
    sort_order: 'desc',
    ...params,
  };

  const queryString = buildPendingBusinessQueryString(queryParams);
  const endpoint = `/api/business${queryString}`;

  const wireData = await apiClient.get<{
    items: BusinessWire[];
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  }>(endpoint);

  const validatedData = paginatedBusinessResponseSchema.parse(wireData);

  return {
    items: validatedData.items.map(mapBusinessWireToDomain),
    total: validatedData.total,
    page: validatedData.page,
    per_page: validatedData.per_page,
    total_pages: validatedData.total_pages,
  };
}

/**
 * Approve a pending business
 */
export async function approveBusiness(id: string): Promise<Business> {
  const wireData = await apiClient.patch<BusinessWire>(`/api/business/${id}/approve`);
  const validatedData = businessWireSchema.parse(wireData);
  return mapBusinessWireToDomain(validatedData);
}

/**
 * Update a business (admin)
 */
export async function updateBusiness(
  id: string,
  data: Record<string, any>
): Promise<Business> {
  const wireData = await apiClient.put<BusinessWire>(`/api/business/${id}`, data);
  const validatedData = businessWireSchema.parse(wireData);
  return mapBusinessWireToDomain(validatedData);
}

/**
 * Delete a business (admin)
 */
export async function deleteBusiness(id: string): Promise<void> {
  await apiClient.delete(`/api/business/${id}`);
}
